import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  addDays, subDays, isValid,
} from 'date-fns';
import { DateRangePicker as DateRangeSelector, Calendar as DatePicker } from 'react-date-range';
import { Calendar, ChevronLeft } from 'react-feather';
import Icon, { IconSizes } from '../icon';
import Button, { ButtonTypes } from '../button';
import { formatDate } from '../../../utils/date-utils';
import useOnClickOutside from '../../enhancers/custom-hook/use-on-click-outside';
import useTranslation from '../../../utils/translation-utils';
import useMobileView from '../../enhancers/custom-hook/use-mobile-view';
import { DatePickerDefaultRanges } from './constants';
import { getDefaultDateRangeMap } from './utils/date-utils';
import Backdrop from '../../enhancers/composer/backdrop';
import ComputePositionComponent from '../compute-position-component';
import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file
import './date-range-picker.scss';
import './theme-overwrite.scss';

const DatePickerDirections = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal',
};

const DatePickerPositions = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

const DatePickerTypes = {
  DATE: 'DATE',
  DATE_RANGE: 'DATE_RANGE',
};

const DateRangePicker = ({
  label, className, startDate, endDate, minDate, maxDate, onRangeSelect,
  onChange, direction, hideInputButton, showLabelInline, defaultRanges,
  backdrop, showInputView, onCancel, type, date, numOfMonths, selectedRangeId,
  placeholder, showRangeTypePickerView, disabled,
}) => {
  const ref = useRef();
  const isMobileView = useMobileView();
  const initialRangesRef = useRef(null);
  const [getTranslation] = useTranslation();
  const [range, setRange] = useState([{
    startDate: startDate ? new Date(startDate) : null,
    endDate: endDate ? new Date(endDate) : new Date(''),
    key: 'selection',
  }]);
  const referenceElement = useRef(null);
  const [isPickerOpen, setIsPickerOpen] = useState(!defaultRanges?.length);
  const [activeRangeId, setActiveRangeId] = useState(null);
  const classes = clsx('date-range-picker-wrapper', { 'no-input': hideInputButton });

  useEffect(() => {
    if (hideInputButton) {
      setIsPickerOpen(true);
    }
  }, [hideInputButton]);

  // selectedRangeId is used to control activeRangeId by parent component. Do not miss use
  useEffect(() => {
    if (selectedRangeId) {
      setActiveRangeId(selectedRangeId);
    }
  }, [selectedRangeId]);

  useEffect(() => {
    if (startDate && endDate) {
      setRange([{
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        key: 'selection',
      }]);
    }

    if (!activeRangeId && !selectedRangeId) {
      if (startDate && endDate) {
        initialRangesRef.current = [new Date(startDate), new Date(endDate)];
      }
      setActiveRangeId('allTime');
    }
  }, [isPickerOpen]);

  const togglePicker = useCallback((isOpen) => {
    if (hideInputButton) return;
    setIsPickerOpen(isOpen);
  }, [isPickerOpen, hideInputButton]);

  const closePicker = () => {
    togglePicker(false);
  };
  useOnClickOutside(ref, closePicker);

  const handleBackdropClick = useCallback(() => {
    togglePicker(!isPickerOpen);
  }, [isPickerOpen, togglePicker]);

  const onInputViewClick = () => {
    if (disabled) {
      return;
    }
    togglePicker(!isPickerOpen);
  };

  const getInputView = () => {
    let value = '';
    if (type === DatePickerTypes.DATE) {
      value = date ? formatDate(date) : '';
    } else if (startDate && endDate) {
      value = `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }
    return (
      <button
        type="button"
        className={clsx(['date-range-picker-button', { disabled }])}
        ref={referenceElement}
        onClick={onInputViewClick}
      >
        {(label && showLabelInline)
          ? <span className="date-range-picker-inline-label">{label}</span>
          : (
            <Icon
              icon={Calendar}
              size={IconSizes.SMALL}
              className="date-range-picker-icon"
            />
          )}
        {value ? <div className="date-range-picker-value">{value}</div>
          : <span className="date-range-picker-placeholder">{placeholder}</span>}
      </button>
    );
  };

  const getRanges = () => {
    const currDate = new Date();

    const ranges = {};

    const defaultRangeDateMap = getDefaultDateRangeMap({ getTranslation, initialRangesRef });

    for (let itr = 0; itr < defaultRanges.length; itr += 1) {
      const defaultRange = defaultRanges[itr];
      if (defaultRangeDateMap[defaultRange]) {
        ranges[defaultRangeDateMap[defaultRange].label] = {
          id: defaultRange,
          range: defaultRangeDateMap[defaultRange].range,
        };
      } else if (defaultRange < 0) {
        const xDaylabel = getTranslation('LAST_X_DAYS', { x: Math.abs(defaultRange) });
        const xDayRange = [
          subDays(currDate, Math.abs(defaultRange) - 1),
          currDate,
        ];
        ranges[xDaylabel] = {
          id: defaultRange,
          range: xDayRange,
        };
      } else if (defaultRange > 0) {
        const xDaylabel = getTranslation('NEXT_X_DAYS', { x: defaultRange });
        const xDayRange = [
          currDate,
          addDays(currDate, Math.abs(defaultRange)),
        ];
        ranges[xDaylabel] = {
          id: defaultRange,
          range: xDayRange,
        };
      }
    }

    return ranges;
  };

  const handleSelectRange = (rangeId, rangeKey) => {
    const ranges = getRanges();
    setActiveRangeId(rangeId);
    if (rangeId !== 'custom') {
      onChange(
        new Date(ranges[rangeKey].range[0]).getTime(),
        new Date(ranges[rangeKey].range[1]).getTime(),
      );
      closePicker();
    }
  };

  const getRangeView = () => {
    if (!showRangeTypePickerView) {
      return null;
    }
    if (hideInputButton || type === DatePickerTypes.DATE) {
      return null;
    }
    const ranges = getRanges();
    return (
      <div className="drp-ranges">
        {Object.keys(ranges).map((rangeKey) => (
          <div
            key={rangeKey}
            className={clsx('drp-range-item', { active: ranges[rangeKey].id === activeRangeId })}
            onClick={() => handleSelectRange(ranges[rangeKey].id, rangeKey)}
          >
            {rangeKey}
          </div>
        ))}
      </div>
    );
  };

  const handleApply = () => {
    onChange(range[0].startDate.getTime(), range[0].endDate.getTime());
    closePicker();
  };

  const handleCancel = () => {
    closePicker();
    onCancel();
  };

  const getDateRangeString = () => {
    const dynStartDate = range[0]?.startDate ? range[0].startDate : startDate;
    const dynEndDate = range[0]?.endDate ? range[0].endDate : endDate;
    let dateStr = '';

    if (isValid(dynStartDate)) {
      dateStr += `${formatDate(dynStartDate)}`;
    }

    if (isValid(dynEndDate)) {
      dateStr += ` - ${formatDate(dynEndDate)}`;
    }
    return dateStr;
  };

  const isApplyBtnEnabled = () => {
    const { startDate: rangeStartDate, endDate: rangeEndDate } = range[0];
    return isValid(rangeStartDate) && isValid(rangeEndDate);
  };

  const getFooterView = () => {
    if (hideInputButton) {
      return null;
    }
    return (
      <div className="drp-footer">
        {!isMobileView && <div className="drp-footer-date">{getDateRangeString()}</div>}
        <div className="drp-footer-btns">
          <Button
            label={getTranslation('DTP_CANCEL')}
            type={ButtonTypes.SECONDARY}
            onClick={handleCancel}
          />
          <Button
            label={getTranslation('DTP_APPLY')}
            type={ButtonTypes.PRIMARY}
            disabled={!isApplyBtnEnabled()}
            onClick={handleApply}
          />
        </div>
      </div>
    );
  };
  const handleOnChange = (item) => {
    setRange([{ ...item.selection }]);
    onRangeSelect(item.selection);
    if (hideInputButton) {
      onChange(item.selection.startDate, item.selection.endDate);
    }
  };
  const handleDatePickerChange = (item) => {
    onChange(item);
    closePicker();
  };

  const getCalendarView = () => {
    const commonProps = {
      showSelectionPreview: true,
      minDate,
      maxDate,
      showDateDisplay: false,
      editableDateInputs: false,
      strictMode: true,
      direction,
    };
    if (type === DatePickerTypes.DATE) {
      return (
        <div className="drp-calendar">
          <DatePicker
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...commonProps}
            onChange={handleDatePickerChange}
            date={date}
            months={numOfMonths}
          />
        </div>
      );
    }
    if (activeRangeId === 'custom' || isMobileView) {
      return (
        <div className="drp-calendar">
          <DateRangeSelector
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...commonProps}
            onChange={handleOnChange}
            moveRangeOnFirstSelection={false}
            ranges={range}
            rangeColors={['#7f56d9']}
            months={isMobileView ? 1 : numOfMonths}
          />
          {getFooterView()}
        </div>
      );
    }

    return null;
  };

  const getBackArrow = () => (
    <div className="modal-back-arrow">
      <Icon icon={ChevronLeft} onClick={() => setIsPickerOpen(false)} />
    </div>
  );

  return (
    <div className={clsx('date-range-picker-cont', className)} ref={ref}>
      {(label && !showLabelInline) && <div className="date-range-picker-label">{label}</div>}
      {showInputView && getInputView()}
      {isPickerOpen && (
        <>
          {isMobileView && backdrop && (
          <Backdrop
            open={isPickerOpen}
            onBackdropClick={handleBackdropClick}
            className="drp-mobile"
          >
            <div className={classes}>
              <div className="drp-mobile-header">
                <div className="header-icon-label">
                  <div className="header-icon">{getBackArrow()}</div>
                  <div className="header-label">
                    {`${getTranslation('FILTER_BY')} : `}
                  </div>
                </div>
                <div className="header-val">{getDateRangeString()}</div>
              </div>
              {getCalendarView()}
            </div>
          </Backdrop>
          )}
          {isMobileView && !backdrop && (
            <div className="drp-without-backdrop">{getCalendarView()}</div>
          )}
          {!isMobileView && (
            <ComputePositionComponent
              parentElementRef={referenceElement}
              reCalculatePosition={activeRangeId}
            >
              <div className={classes}>
                {getCalendarView()}
                {getRangeView()}
              </div>
            </ComputePositionComponent>
          )}
        </>
      )}
    </div>
  );
};

DateRangePicker.defaultProps = {
  label: '',
  className: '',
  date: null,
  startDate: null,
  endDate: null,
  minDate: new Date('01/01/2000'),
  maxDate: new Date(),
  onChange: () => {},
  onRangeSelect: () => {},
  direction: DatePickerDirections.HORIZONTAL,
  hideInputButton: false,
  showLabelInline: false,
  defaultRanges: Object.values(DatePickerDefaultRanges),
  selectedRangeId: '',
  backdrop: false,
  showInputView: true,
  onCancel: () => {},
  type: DatePickerTypes.DATE_RANGE,
  numOfMonths: 2,
  placeholder: '',
  showRangeTypePickerView: true,
  disabled: false,
};

DateRangePicker.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  startDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  endDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func,
  onRangeSelect: PropTypes.func,
  direction: PropTypes.oneOf(Object.values(DatePickerDirections)),
  hideInputButton: PropTypes.bool,
  showLabelInline: PropTypes.bool,
  defaultRanges: PropTypes.arrayOf(PropTypes.string),
  selectedRangeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  backdrop: PropTypes.bool,
  showInputView: PropTypes.bool,
  onCancel: PropTypes.func,
  type: PropTypes.oneOf(Object.values(DatePickerTypes)),
  numOfMonths: PropTypes.number,
  placeholder: PropTypes.string,
  showRangeTypePickerView: PropTypes.bool,
  disabled: PropTypes.bool,
};

export {
  DateRangePicker as default,
  DatePickerDirections,
  DatePickerPositions,
  DatePickerDefaultRanges,
  DatePickerTypes,
};
